import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import type { ProfileCard } from '@/types/qriots';
import { BadgeChip } from './BadgeChip';
import { FreshnessLabel } from './FreshnessLabel';

interface LorProfileCardProps {
  data: ProfileCard;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  return `${m}m`;
}

function formatPlayedAt(iso: string, t: TFunction): string {
  const diffMs = Date.now() - Date.parse(iso);
  const h = Math.floor(diffMs / 1000 / 3600);
  if (h < 24) return t('playedAt.hours', { n: h });
  return t('playedAt.days', { n: Math.floor(h / 24) });
}

export function LorProfileCard({ data }: LorProfileCardProps) {
  const { t } = useTranslation('riot');

  return (
    <div className="mx-4 flex flex-col gap-4 rounded-xl bg-bg-secondary p-4">
      {/* Header */}
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-fg">
          {data.summonerLevel ? t('profile.level', { level: data.summonerLevel }) : ''} · {data.region}
        </p>
        <FreshnessLabel freshness={data.freshness} />
      </div>

      {/* Last match — LoR uses factions instead of championName */}
      {data.lastMatch && (
        <section>
          <p className="text-[10px] font-bold uppercase tracking-wider text-fg-muted">
            {t('title.lastMatch')}
          </p>
          <p className="mt-1 text-sm text-fg">
            {data.lastMatch.factions?.join(' / ') ?? '—'} ·{' '}
            <span className={data.lastMatch.win ? 'text-success' : 'text-fg-muted'}>
              {data.lastMatch.win ? t('match.win') : t('match.loss')}
            </span>
            {' · '}
            {formatDuration(data.lastMatch.gameDuration)} · {formatPlayedAt(data.lastMatch.playedAt, t)}
          </p>
        </section>
      )}

      {/* Badges */}
      {data.badges && data.badges.length > 0 && (
        <section>
          <p className="text-[10px] font-bold uppercase tracking-wider text-fg-muted">
            {t('title.badges')}
          </p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {data.badges.map((b) => (
              <BadgeChip key={b.code} badge={b} size="sm" />
            ))}
          </div>
        </section>
      )}

      <p className="text-[10px] leading-relaxed text-fg-muted">{data.disclaimer}</p>
    </div>
  );
}
